import Loader from '../../../components/common/Loader';
import { InfoState } from '../../../components/ui';
import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Dialog } from '../../../components/ui';
import { Pagination, Search, TableHeader, TableBody } from '../../../components/table';
import ActionIconButton from '../../../components/ui/ActionIconButton';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Icon } from '@iconify/react';
import { useSearchParams } from 'react-router-dom';
import NoData from '../../../components/common/NoData';
import { toast } from '../../../components/ui/notify';
import { getAllTicket, postRaiseTicketV2, updateTicket, deleteTicket } from '../../../services/user/help.services';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function Ticket() {
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [searchParams] = useSearchParams();
  const { timeZone } = useTimezone();
  const formatTicketDate = (value) => formatUtcInTimeZone(value, timeZone, 'MMM DD, YYYY');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [ticketData, setTicketData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalCount: 0 });

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);

  const [subject, setSubject] = useState('');
  const [summary, setSummary] = useState('');
  const [errors, setErrors] = useState({ subject: '', summary: '' });

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [raising, setRaising] = useState(false);
  const [hasAutoOpenedTicket, setHasAutoOpenedTicket] = useState(false);

  useEffect(() => {
    getTicketData();
  }, [page, limit, searchTerm, statusFilter]);

  useEffect(() => {
    if (hasAutoOpenedTicket) {
      return;
    }

    const ticketIdFromUrl = (searchParams.get('ticketId') || '').trim();
    if (!ticketIdFromUrl || !ticketData.length) {
      return;
    }

    const matchedTicket = ticketData.find(
      (ticket) => String(ticket.ticket_id).toLowerCase() === ticketIdFromUrl.toLowerCase()
    );

    if (matchedTicket) {
      setSelectedTicket(matchedTicket);
      setShowViewModal(true);
      setHasAutoOpenedTicket(true);
    }
  }, [searchParams, ticketData, hasAutoOpenedTicket]);

  const getTicketData = async () => {
    setLoading(true); setUnavailable(false);
    try {
      const payload = { page, limit, search: searchTerm, status: statusFilter };
      const response = await getAllTicket(payload);
      if (response.success && response.data) {
        setTicketData(response.data.tickets);
        setPagination(response.data.pagination);
      } else { setUnavailable(true); }
    } catch (error) {
      console.warn('[Support tickets]', error); setUnavailable(true);
    } finally { setLoading(false); }
  };

  const canEditTicket = (ticket) => {
    const status = String(ticket?.status || '').trim().toLowerCase();
    return status === 'pending' || status === 'in progress';
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { tone: 'warning', icon: 'mdi:clock-outline', label: 'Pending' },
      solved: { tone: 'success', icon: 'mdi:check-circle-outline', label: 'Solved' },
      'in progress': { tone: 'info', icon: 'mdi:progress-clock', label: 'In Progress' },
      cancelled: { tone: 'danger', icon: 'mdi:close-circle-outline', label: 'Cancelled' },
    };
    const variant = variants[String(status || '').trim().toLowerCase()] || {
      tone: 'neutral', icon: 'mdi:help-circle-outline', label: 'Unknown',
    };
    return (
      <StatusBadge tone={variant.tone}>
        <Icon icon={variant.icon} className="tw-text-base" aria-hidden="true" />
        {variant.label}
      </StatusBadge>
    );
  };

  const handleRaiseTicket = async () => {
    const newErrors = { subject: '', summary: '' };

    if (!subject.trim()) newErrors.subject = 'Subject is required';
    if (!summary.trim()) newErrors.summary = 'Summary is required';

    setErrors(newErrors);
    if (Object.values(newErrors).some(msg => msg)) return;

    setRaising(true);
    const payload = { subject: subject.trim(), description: summary.trim() };

    setTimeout(async () => {
      try {
        const response = editingTicket
          ? await updateTicket(editingTicket.ticket_id, payload)
          : await postRaiseTicketV2(payload);
        if (response.success) {
          toast.success(editingTicket ? 'Ticket updated successfully' : 'Ticket raised successfully');
          getTicketData();
          setShowModal(false);
          setEditingTicket(null);
          setSubject('');
          setSummary('');
        } else toast.error(response.message || response.error?.message || 'Something went wrong');
      } catch (error) {
        toast.error(error.response?.data?.message || error.message || 'Internal server error');
      } finally {
        setRaising(false);
      }
    }, 2000);
  };

  const handleEditTicket = (ticket) => {
    if (!canEditTicket(ticket)) {
      toast.info('Solved or cancelled tickets cannot be edited');
      return;
    }
    setEditingTicket(ticket);
    setSubject(ticket.subject || '');
    setSummary(ticket.description || '');
    setErrors({ subject: '', summary: '' });
    setShowModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);

    setTimeout(async () => {
      try {
        const response = await deleteTicket(ticketToDelete.ticket_id);
        if (response.success) {
          toast.success('Ticket deleted successfully');
          getTicketData();
        } else {
          toast.error(response.message || response.error?.message || 'Something went wrong');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'Internal server error');
      } finally {
        setDeleting(false);
        setShowDeleteModal(false);
        setTicketToDelete(null);
      }
    }, 2000);
  };


  return (
    <>
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-py-4"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full tw-mb-4">
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center">
            <h4 className="tw-mb-2 md:tw-mb-0">Ticket List</h4>
            <Button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-px-6"}
              style={{ whiteSpace: 'nowrap', width: 'auto' }}  // ✅ avoid full width
              onClick={() => setShowModal(true)}
            >
              Raise Ticket
            </Button>
          </div>
        </div>


        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-flex-wrap">
            <div className="tw-w-full tw-max-w-[350px]">
              <Search
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPageReset={() => setPage(1)}
                className="tw-rounded-xl"
              />
            </div>
            <Form.Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="tw-rounded-xl"
              style={{ width: '180px' }}
              aria-label="Filter tickets by status"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Solved">Solved</option>
              <option value="Cancelled">Cancelled</option>
            </Form.Select>
          </div>

          <div className="tw-min-w-0">
            {loading ? <Loader variant="table" rows={5} columns={5} /> : unavailable ? <InfoState title="This list is temporarily unavailable" onRetry={getTicketData} /> : (
              <Table responsive className="tw-min-w-[700px] tw-table-fixed">
                <TableHeader className="[--tw-bg-opacity:1] tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-table-primary">
                  <tr>
                    <th className="tw-w-[70px]">SL</th>
                    <th>Title</th>
                    <th className="tw-w-[145px]">Status</th>
                    <th className="tw-w-[160px]">Submitted Date</th>
                    <th className="tw-w-[155px] tw-text-center">Action</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {ticketData.length > 0 ? (
                    ticketData.map((item, index) => (
                      <tr key={item.ticket_id}>
                        <td>{(page - 1) * limit + index + 1}</td>
                        <td>
                          <div className="tw-overflow-hidden tw-break-words [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]" title={item.subject || 'Untitled ticket'}>
                            {item.subject || 'Untitled ticket'}
                          </div>
                        </td>
                        <td>{getStatusBadge(item.status)}</td>
                        <td>{formatTicketDate(item.createdAt)}</td>
                        <td>
                          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                            <ActionIconButton icon="mdi:eye-outline" label="View ticket" onClick={() => { setSelectedTicket(item); setShowViewModal(true); }} />
                            {canEditTicket(item) && (
                              <ActionIconButton icon="fluent:edit-24-regular" label="Edit ticket" tone="edit" onClick={() => handleEditTicket(item)} />
                            )}
                            <ActionIconButton icon="fluent:delete-24-regular" label="Delete ticket" tone="danger" onClick={() => { setTicketToDelete(item); setShowDeleteModal(true); }} />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="tw-p-0">
                        <NoData message="No tickets found." />
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          <Pagination
            page={page}
            pageSize={limit}
            buttonSize="sm"
            totalRecords={pagination.totalCount}
            totalPages={pagination.totalPages}
            disabled={loading || unavailable}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
          />
        </div>
      </div>

      {/* View Modal */}
      <Dialog show={showViewModal} onHide={() => setShowViewModal(false)}>
        <Dialog.Header closeButton><Dialog.Title>Ticket Details</Dialog.Title></Dialog.Header>
        <Dialog.Body>
          {selectedTicket && (
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div><strong>Ticket ID:</strong><div>{selectedTicket.ticket_id}</div></div>
              <div><strong>Subject:</strong><div>{selectedTicket.subject}</div></div>
              <div><strong>Summary:</strong><div>{selectedTicket.description}</div></div>
              <div><strong>Response:</strong><div>{selectedTicket.response || 'No response yet'}</div></div>
              <div><strong>Status:</strong><div>{selectedTicket.status}</div></div>
              <div><strong>Submitted Date:</strong><div>{formatTicketDate(selectedTicket.createdAt)}</div></div>
            </div>
          )}
        </Dialog.Body>
        <Dialog.Footer><Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button></Dialog.Footer>
      </Dialog>

      {/* Delete Modal */}
      <Dialog show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Dialog.Header closeButton><Dialog.Title>Confirm Deletion</Dialog.Title></Dialog.Header>
        <Dialog.Body>
          Are you sure you want to delete this ticket?
          <br />
          <strong>Ticket ID: {ticketToDelete?.ticket_id}</strong>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? (<><span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" /> Deleting...</>) : 'Delete'}
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* Raise Ticket Modal */}
      <Dialog show={showModal} onHide={() => { setShowModal(false); setEditingTicket(null); }}>
        <Dialog.Header closeButton><Dialog.Title>{editingTicket ? 'Edit Ticket' : 'Raise New Ticket'}</Dialog.Title></Dialog.Header>
        <Dialog.Body>
          <Form>
            <Form.Group className="tw-mb-4">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                isInvalid={!!errors.subject}
              />
              {errors.subject && <Form.Text className="!tw-text-[var(--danger-color)] tw-text-rose-600">{errors.subject}</Form.Text>}
            </Form.Group>

            <Form.Group className="tw-mb-4">
              <Form.Label>Summary</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                isInvalid={!!errors.summary}
                maxLength={2000}
              />
              {errors.summary && <Form.Text className="!tw-text-[var(--danger-color)] tw-text-rose-600">{errors.summary}</Form.Text>}
            </Form.Group>
          </Form>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); setEditingTicket(null); }} disabled={raising}>Close</Button>
          <Button variant="primary" onClick={handleRaiseTicket} disabled={raising}>
            {raising ? (<><span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" /> {editingTicket ? 'Updating...' : 'Raising...'}</>) : editingTicket ? 'Update Ticket' : 'Raise Ticket'}
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}

export default Ticket;
